import {
  CareerService,
  MSGraphService,
  ProfileService,
  UsageUserService,
  CopilotService,
  KnowledgeCloudService,
  RoleLibraryService,
} from '@careerhub/api';
import { ReducerRegistry, ModuleStoreBuilder } from '../builder';
import { IModuleStoreBuilderResult, ICareerHubModuleContext, ICareerHubModuleConfig } from '../models';
import { FeatureFlightClient, IFeatureFlight, AppInsightsClient } from '../services';
import {
  UserProfileState,
  userProfileStateReducer,
  CareerState,
  careerStateReducer,
  userProfileSagas,
  careerSagas,
  CopilotState,
  copilotStateReducer,
  copilotSagas,
} from '../store';

/** Initialization logic for the Career Hub (access checks and copilot initialization) */
export namespace careerHubModule {
  let initialized: boolean = false;
  let storeBuilderResult: IModuleStoreBuilderResult<ICareerHubModuleContext, {}>;

  /** Returns whether module is initialized. */
  export function isInitialized(): boolean {
    return initialized;
  }

  /** Initializes Career Hub: checks user's access and initializes Copilot. */
  export function initialize(
    config: ICareerHubModuleConfig
  ): Promise<IModuleStoreBuilderResult<ICareerHubModuleContext, {}>> {
    return new Promise<IModuleStoreBuilderResult<ICareerHubModuleContext, {}>>(async (resolve, reject) => {
      try {
        if (!initialized) {
          const apiResources = config.apiResources;

          /** Config to get tokens using this app's client ID.  */
          const careerHubConfig = {
            getAuthorization: config.careerHubTokenProvider,
            AdditionalHeaders: {
              'Ocp-APIM-Subscription-Key': __HR_APIM_SUBSCRIPTION_KEY__,
            },
          };

          const careerService = new CareerService(careerHubConfig, apiResources.careerService.resourceBaseUrl);
          const flightData = await careerService.checkAccess();
          const featureService = new FeatureFlightClient(flightData as IFeatureFlight);
          const graphService = new MSGraphService(apiResources.msGraphApi, config.instance);
          const hrCoreService = new ProfileService(apiResources.profileService, config.instance);

          const telemetryClient = new AppInsightsClient(config.appInsightsConfig);
          const usageUserService = new UsageUserService(apiResources.usageUserService, config.instance);
          const usageUserId = usageUserService.getAnonymizedUserId();
          telemetryClient.setUsageUserId(usageUserId);
          const copilotService = new CopilotService(
            apiResources.copilotService,
            config.instance,
            usageUserId.then((id) => {
              return id.usageUserId;
            }),
            flightData
          );

          const roleLibraryServiceConfig = {
            ...apiResources.roleLibraryService,
            headers: config.hrApimSubscriptionKey
              ? {
                  'Ocp-Apim-Subscription-Key': config.hrApimSubscriptionKey,
                }
              : undefined,
          };
          const roleLibraryService = new RoleLibraryService(roleLibraryServiceConfig, config.instance);

          const knowledgeCloudService = new KnowledgeCloudService(apiResources.knowledgeCloudService, config.instance);
          const reducerRegistry = new ReducerRegistry()
            .register(CareerState, careerStateReducer)
            .register(CopilotState, copilotStateReducer)
            .register(UserProfileState, userProfileStateReducer);

          // build redux store
          // TODO disable debug mode for prod build (small impact on performance)
          storeBuilderResult = new ModuleStoreBuilder<ICareerHubModuleContext, {}>(reducerRegistry, {}, true)
            .configureSaga({
              telemetryClient,
              graphService,
              hrCoreService,
              featureService,
              careerService,
              roleLibraryService,
              copilotService,
              navigationService: config.navigationService,
              knowledgeCloudService,
            })
            .addRootSagas([userProfileSagas, careerSagas, copilotSagas])
            .build();
          initialized = true;
        }
        resolve(storeBuilderResult);
      } catch (e) {
        reject(e);
      }
    });
  }
}
